// Pure helpers shared by the map modules: solid builders, spawn rings, footprint tests, the neutral
// hazard pose, telegraph list entries, progress curves and seeded per-round parameters. This module
// imports no sim code beyond the leaf `sim/dmath` (deterministic trig: Node and Chrome disagree in
// the last bits of `Math.sin/cos/atan2`, and everything that feeds world state must agree for the
// §30.4 hash test), so map modules can build their static solids at load time from any entry point
// (the sim-dependent hazard helpers live in `hazards.ts`). Static solids are built once and handed
// out by reference; kinematic solids are module scratch refreshed by `solids()`.
import { DEG, distToSegmentSq } from '../math';
import { rand, seedRng, type RngHolder } from '../rng';
import { dcos, dsin } from '../sim/dmath';
import type { Vec2 } from '../types';
import { SolidKind, createSolid, type HazardPose, type Solid } from './mapTypes';

export function circleSolid(x: number, y: number, r: number, top: number, restitution: number, tag: number, index: number): Solid {
  const s = createSolid();
  s.kind = SolidKind.CIRCLE;
  s.ax = x;
  s.ay = y;
  s.r = r;
  s.top = top;
  s.restitution = restitution;
  s.tag = tag;
  s.index = index;
  return s;
}

export function capsuleSolid(ax: number, ay: number, bx: number, by: number, r: number, top: number, restitution: number, tag: number, index: number): Solid {
  const s = createSolid();
  s.kind = SolidKind.CAPSULE;
  s.ax = ax;
  s.ay = ay;
  s.bx = bx;
  s.by = by;
  s.r = r;
  s.top = top;
  s.restitution = restitution;
  s.tag = tag;
  s.index = index;
  return s;
}

/** An axis-aligned box as 4 thin capsules along its edges (§18.3: the vending machine). */
export function boxSolids(cx: number, cy: number, width: number, depth: number, edgeRadius: number, top: number, restitution: number, tag: number): Solid[] {
  const x0 = cx - width / 2;
  const x1 = cx + width / 2;
  const y0 = cy - depth / 2;
  const y1 = cy + depth / 2;
  return [
    capsuleSolid(x0, y0, x1, y0, edgeRadius, top, restitution, tag, 0),
    capsuleSolid(x1, y0, x1, y1, edgeRadius, top, restitution, tag, 1),
    capsuleSolid(x1, y1, x0, y1, edgeRadius, top, restitution, tag, 2),
    capsuleSolid(x0, y1, x0, y0, edgeRadius, top, restitution, tag, 3),
  ];
}

/** `count` points evenly spaced on a circle, the first at `startDeg`. */
export function ringPoints(radius: number, startDeg: number, count: number): Vec2[] {
  return Array.from({ length: count }, (_, k) => {
    const a = (startDeg + (k * 360) / count) * DEG;
    return { x: radius * dcos(a), y: radius * dsin(a) };
  });
}

/** Copies static solids into the caller's reusable array; returns the count. */
export function copySolids(src: readonly Solid[], out: Solid[]): number {
  for (let i = 0; i < src.length; i++) out[i] = src[i];
  return src.length;
}

/** Distance from (x, y) to the surface of solid `s` (negative inside). */
export function solidDistance(s: Solid, x: number, y: number): number {
  const d2 = s.kind === SolidKind.CIRCLE ? (x - s.ax) ** 2 + (y - s.ay) ** 2 : distToSegmentSq(x, y, s.ax, s.ay, s.bx, s.by);
  return Math.sqrt(d2) - s.r;
}

/** True if (x, y) lies within `pad` of any solid's footprint (crate placement exclusion). */
export function nearSolid(solids: readonly Solid[], x: number, y: number, pad: number): boolean {
  for (const s of solids) if (solidDistance(s, x, y) <= pad) return true;
  return false;
}

/** Writes the neutral pose (no hazards active) into `out`, including the additive WP2 fields. */
export function neutralHazardPose(out: HazardPose): HazardPose {
  out.armAngles.fill(0);
  out.armCount = 0;
  out.armDir = 1;
  out.flipperAngles[0] = 0;
  out.flipperAngles[1] = 0;
  for (const b of out.balls) {
    b.x = 0;
    b.y = 0;
    b.active = false;
  }
  for (const b of out.bubbles) {
    b.x = 0;
    b.y = 0;
    b.scale = 0;
    b.wobble = 0;
  }
  out.cupAngle = 0;
  out.candleFlicker.fill(0);
  out.candleFlame.fill(0);
  out.armOmega = 0;
  out.armDrop = 0;
  out.reverseWarn = 0;
  out.flipperWarn = 0;
  out.flipperLights = false;
  out.ballWarn = 0;
  out.ballRoll?.fill(0);
  out.wedgeCrack?.fill(0);
  out.wedgeFallSec?.fill(-1);
  out.sdNextTile = -1;
  out.sdNextWarn = 0;
  out.telegraphCount = 0;
  if (out.telegraphs) {
    for (const t of out.telegraphs) {
      t.kind = 0;
      t.index = 0;
      t.warnTick = 0;
      t.fireTick = 0;
      t.x = 0;
      t.y = 0;
    }
  }
  return out;
}

/**
 * Appends a telegraph to the pose list (ignored when the pose has no list or it is full). Maps add
 * every hazard whose warning window contains the pose tick.
 */
export function addTelegraph(out: HazardPose, kind: number, index: number, warnTick: number, fireTick: number, x: number, y: number): void {
  const list = out.telegraphs;
  const n = out.telegraphCount ?? 0;
  if (!list || n >= list.length) return;
  const t = list[n];
  t.kind = kind;
  t.index = index;
  t.warnTick = warnTick;
  t.fireTick = fireTick;
  t.x = x;
  t.y = y;
  out.telegraphCount = n + 1;
}

/** 0..1 progress of `t` through [start, end) (0 before, 1 after). */
export function progress(t: number, start: number, end: number): number {
  if (t <= start) return 0;
  if (t >= end) return 1;
  return (t - start) / (end - start);
}

const seedHolder: RngHolder = { rng: 0 };

/**
 * A deterministic stream for per-round hazard parameters, derived from the round seed and a salt.
 * It never touches the world RNG, so poses stay pure functions of (seed, tick) on every client.
 * The holder is shared scratch: read what you need before asking for another stream.
 */
export function seededStream(seed: number, salt: number): RngHolder {
  seedHolder.rng = seedRng((seed ^ Math.imul(salt + 0x51ed27, 0x9e3779b1)) >>> 0);
  rand(seedHolder);
  return seedHolder;
}
